/**
 * What a fact screen needs to know about who is using it, and where to send it.
 *
 * The four fact screens - 施術・強み (Catalog), ペルソナ, 監修医, CTA - are the
 * clinic's own, and the JMC clinic page renders the SAME panels. It used to
 * have a form of its own built from a field list, which drifted from these
 * screens until the two asked for different things. Now the only differences
 * are the three below:
 *
 *   `base`  - '' for the clinic, `/admin/clinics/:id` for JMC. The server
 *             mounts the clinic's own routes again under that prefix (see
 *             server/app.ts), so every path is simply `${base}/personas/...`.
 *   `scope` - who may write where. From the session on the clinic's side; on
 *             JMC's, clinic-admin authority over every store, which is what the
 *             server's `actForClinic` resolves as well.
 */
import type { ReactNode } from 'react';
import type { Session } from './api.ts';

export interface FactScope {
  isClinicAdmin: boolean;
  stores: Array<{ id: string; name: string }>;
  /** The store the view is narrowed to, if any. */
  activeStore?: { id: string; name: string } | undefined;
}

export interface FactPanelProps<Row> {
  items: Row[];
  base: string;
  scope: FactScope;
  /** Set on the JMC page, so 「リンクから追加」 uses the JMC mount. */
  clinicId?: string;
  /** Rendered at the top of the panel's own card. See Card's `lead`. */
  lead?: ReactNode;
}

export function sessionScope(session: Session): FactScope {
  const clinic = session.memberships.find((m) => m.clinicId === session.activeClinicId);
  const stores = clinic?.stores ?? [];
  return {
    isClinicAdmin: clinic?.clinicAdmin ?? false,
    stores,
    activeStore: stores.find((store) => store.id === session.activeStoreId),
  };
}

/**
 * Whether a row belongs in the chosen scope view.
 *
 * The clinic's loader already asks the server for exactly this set, so on its
 * screen the filter changes nothing. The JMC page loads every row once and
 * narrows here - the same four views, the same rule as the server's CASE.
 */
export function inScope(
  row: { storeId?: string | null },
  view: string,
  activeStoreId: string | undefined,
): boolean {
  const own = row.storeId ?? null;
  if (view === 'clinic') return own === null;
  if (view === 'store') return own !== null && own === activeStoreId;
  if (view === 'effective') return own === null || own === activeStoreId;
  return true;
}
